import * as React from "react";
import { useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import styles from "./TurnrInput.module.scss";
import classnames from "classnames";
import classNames from "classnames";
import { COLORS } from "rylak-common/src/constants";
import { isInputValueChanged, isValueEmpty } from "../../utils";
import lodashDebounce from "lodash/debounce";
import { TurnrTooltip, TurnrTooltipPortalAnchor } from "rylak-common/src/components/TurnrTooltip/TurnrTooltip";
import useKeyDownDetector, { KEY_NAME_ENTER } from "rylak-common/src/hooks/useKeyDownDetector.jsx";
import { interFont } from "../../app/_apps/fonts.jsx";
import _ from "lodash";
import TurnrCloseIconButton from "../buttons/icon-buttons/TurnrCloseIconButton.jsx";
import RoundInfoIcon from "../icons/RoundInfoIcon";
import { faEye, faEyeSlash } from "@fortawesome/pro-regular-svg-icons";
import { TurnrFontAwesomeIconButtonRound } from "~/components/buttons/icon-buttons/TurnrFontAwesomeIconButton";

const debouncedOnChange = lodashDebounce((value, onChange) => {
    onChange(value);
}, 200);

function CurrencyPrefix({ currencyCode }) {
    return (
        <span
            style={{
                fontFamily: interFont.style.fontFamily,
                fontSize: "12px",
                userSelect: "none",
                pointer: "initial",
            }}
        >
            {currencyCode}
        </span>
    );
}

function TurnrInput(props) {
    const {
        value,
        originalValue,
        onChange,
        label,
        title = label,
        placeholder,
        labelLocked = true,
        debounce = true,
        fullWidth,
        disabled,
        type = "text",
        size = "small",
        currencyCode,
        name,
        activeTooltip,
        tooltipPlacement = "top-left",
        tooltip,
        tooltipTitle = title,
        color = COLORS.MID_BROWN,
        borderColor,
        labelColor,
        noLeftBox,
        style,
        inputStyle,
        className,
        onClick,
        onEnter,
        autoFocus = false,
        onLostFocus,
        leftIcon,
        rightIcon,
        rightIconClickable,
        fieldsetStyle,
        required,
        hideRequired,
        rows = "1",
        step = "any",
        autoHeight,
        min,
        max,
        error,
        helperText,
        readOnly,
        showCloseButton = false,
        dataTestId,
        ref: forwardedRef,
    } = props;

    const localRef = useRef(null);

    const onInput = useCallback(() => {
        if (autoHeight) {
            const textArea = localRef?.current;
            textArea.style.height = "";
            textArea.style.height = Math.min(textArea.scrollHeight + 1, 150) + "px";
        }
    }, [autoHeight, localRef?.current]);

    useEffect(() => {
        onInput();
    }, [value, onInput]);

    // function setRefs(el) {
    //     localRef.current = el;
    //     if (typeof forwardedRef === "function") {
    //         forwardedRef(el);
    //     } else if (forwardedRef && typeof forwardedRef === "object") {
    //         forwardedRef.current = el;
    //     }
    // }

    const [localValue, setLocalValue] = useState(_.isNil(value) ? "" : value);
    const [latestUpdatedValue, setLatestUpdatedValue] = useState(localValue);
    const [latestCheckedValue, setLatestCheckedValue] = useState(latestUpdatedValue);

    const localOnChange = useCallback(
        (value) => {
            setLatestUpdatedValue(value);
            onChange(value);
        },
        [onChange],
    );

    useEffect(() => {
        if (debounce) {
            if (localValue !== value) {
                debouncedOnChange(localValue, localOnChange);
            }
        } else {
            localOnChange(localValue);
        }
    }, [localValue]);

    useEffect(() => {
        if (value === latestCheckedValue) {
            return;
        }
        if (localValue !== value && latestUpdatedValue !== value) {
            setLocalValue(value);
        }
        setLatestCheckedValue(value);
    }, [value, localValue, latestUpdatedValue, latestCheckedValue]);

    const inputOnChange = useCallback(
        (value) => {
            if (type === "number") {
                if (value === "") {
                    setLocalValue(null);
                } else {
                    setLocalValue(_.toNumber(value));
                }
            } else {
                setLocalValue(value);
            }
        },
        [type],
    );

    const valueExists = !isValueEmpty(localValue);
    const computedColor = disabled ? COLORS.GREY : color;

    const hasOriginalValue = originalValue !== undefined;
    const valueChanged = hasOriginalValue && isInputValueChanged(originalValue, localValue);

    const tooltipPortalRef = useRef();

    const [focused, setFocused] = React.useState(false);
    const onFocus = () => activeTooltip && setFocused(true);
    const onBlur = () => {
        activeTooltip && setFocused(false);
        onLostFocus && onLostFocus();
    };

    useKeyDownDetector((event) => {
        if (event.key === KEY_NAME_ENTER) {
            if (window.document.activeElement === localRef.current) {
                onEnter && onEnter(localValue);
            }
        }
    });

    useImperativeHandle(forwardedRef, () => ({
        isFocused() {
            return localRef.current === document.activeElement;
        },
        getElement() {
            return localRef?.current;
        },
        hasChanges() {
            return valueChanged;
        },
    }));

    const InputOrTextArea = autoHeight || (rows && rows !== "1" && rows !== 1) ? "textarea" : "input";

    const actualTitle = title && required && !hideRequired ? title + " *" : title;

    const isPassword = type === "password";
    const [showPassword, setShowPassword] = useState(false);

    let rightIconsCount = 0;
    if (showCloseButton) {
        rightIconsCount += 1;
    }
    if (tooltip) {
        rightIconsCount += 1;
    }
    if (rightIcon) {
        rightIconsCount += 1;
    }
    if (isPassword) {
        rightIconsCount += 1;
    }

    const hasAtLeastOneRightIcon = rightIconsCount > 0;

    return (
        <TurnrTooltipPortalAnchor ref={tooltipPortalRef} placement={tooltipPlacement} style={{ width: fullWidth && "100%" }}>
            <div
                onClick={onClick}
                className={classnames(
                    styles["container"],
                    error && styles["error"],
                    valueChanged && styles["value-changed"],
                    disabled && styles["disabled"],
                    readOnly && styles["read-only"],
                    styles["size-" + size],
                    className,
                )}
                style={{ width: fullWidth && "100%", ...style }}
            >
                <InputOrTextArea
                    disabled={disabled || readOnly}
                    data-has-value={valueExists}
                    onInput={onInput}
                    ref={localRef}
                    rows={rows}
                    autoFocus={autoFocus}
                    step={step}
                    min={min}
                    max={max}
                    required={required}
                    placeholder={placeholder}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    className={classnames(
                        styles["input"],
                        valueExists && styles["input-has-value"],
                        (leftIcon || currencyCode) && styles["input-left-icon"],
                        hasAtLeastOneRightIcon && styles["input-right-icon"],
                        rightIconsCount > 0 && styles["input-right-icon-" + rightIconsCount],
                        type === "color" && styles["input-type-color"],
                    )}
                    style={{ color: computedColor, ...inputStyle }}
                    value={localValue}
                    type={showPassword ? "text" : type}
                    name={name}
                    onChange={(e) => inputOnChange(e.target.value, e)}
                    data-test-id={dataTestId}
                />
                {actualTitle && (
                    <label className={classnames(styles["floating-label"], labelLocked && styles["label-locked"])} style={{ color: labelColor }}>
                        {actualTitle}{" "}
                    </label>
                )}
                {!noLeftBox && <div className={styles["value-changed-box"]} style={{ backgroundColor: computedColor }} />}
                <fieldset className={classnames(styles["fieldset"], !actualTitle && styles["no-title"])} tabIndex={"-1"} style={{ borderColor: borderColor, ...fieldsetStyle }}>
                    <legend className={classnames(styles["legend"])}>
                        <span>{actualTitle}</span>
                    </legend>
                </fieldset>
                {(leftIcon || currencyCode) && <div className={styles["left-icon"]}>{currencyCode ? <CurrencyPrefix currencyCode={currencyCode} /> : leftIcon}</div>}
                {hasAtLeastOneRightIcon && (
                    <div className={classNames(styles["right-icon"], (rightIconClickable || isPassword) && styles["right-icon-clickable"], tooltip && styles["right-icon-with-tooltip"])}>
                        {isPassword && (
                            <div>
                                <TurnrFontAwesomeIconButtonRound
                                    icon={showPassword ? faEyeSlash : faEye}
                                    size={"small"}
                                    iconSize={"large"}
                                    onClick={() => {
                                        setShowPassword(!showPassword);
                                    }}
                                />
                                {/*<FontAwesomeIcon icon={faEye} />*/}
                            </div>
                        )}
                        {showCloseButton && (
                            <div
                                style={{
                                    visibility: !value && "hidden",
                                }}
                                className={classnames(styles["close-btn"], (rightIcon || tooltip) && styles["close-btn-has-right-icon"])}
                            >
                                <TurnrCloseIconButton
                                    disabled={disabled || readOnly}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        onChange("");
                                    }}
                                />
                            </div>
                        )}
                        {rightIcon}
                        {tooltip && (
                            <TurnrTooltip portalRef={tooltipPortalRef} active={focused} content={tooltip} title={tooltipTitle} style={{ height: "100%" }}>
                                <RoundInfoIcon />
                            </TurnrTooltip>
                        )}
                    </div>
                )}
            </div>
            {helperText && <div className={classNames(styles["helper-text"], error && styles["helper-text-error"])}>{helperText}</div>}
        </TurnrTooltipPortalAnchor>
    );
}

export default TurnrInput;
